// src/components/edge-nodes/EdgeNodeRegistration.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
//add curly braces
import { edgeNodesService } from '../../services/edge-nodes';

const EdgeNodeRegistration = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    edge_node_id: '',
    hostname: '',
    firmware_version: '',
    ot_sentry_agent_version: '',
    location_info: {
      site: '',
      building: '',
      room: ''
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      location_info: {
        ...prev.location_info,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Clean up the location info to remove empty values
      const locationInfo = {};
      for (const [key, value] of Object.entries(formData.location_info)) {
        if (value.trim() !== '') {
          locationInfo[key] = value;
        }
      }
      
      // Prepare the data for submission
      const registerData = {
        ...formData,
        location_info: Object.keys(locationInfo).length > 0 ? locationInfo : null
      };
      
      const result = await edgeNodesService.registerEdgeNode(registerData);
      
      setSuccess(true);
      
      // Redirect to the edge node detail page after a short delay
      setTimeout(() => {
        navigate(`/edge-nodes/${result.edge_node_id}`);
      }, 1500);
    } catch (err) {
      console.error('Error registering edge node:', err);
      setError('Failed to register edge node. Please check your input and try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Register New Edge Node
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Register a new Celerway edge node device to the AI Sentry Management system.
          </p>

          {success && (
            <div className="mt-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Edge node successfully registered! Redirecting...
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="edge_node_id" className="block text-sm font-medium text-gray-700">
                  Edge Node ID *
                </label>
                <input
                  type="text"
                  name="edge_node_id"
                  id="edge_node_id"
                  required
                  value={formData.edge_node_id}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="hostname" className="block text-sm font-medium text-gray-700">
                  Hostname
                </label>
                <input
                  type="text"
                  name="hostname"
                  id="hostname"
                  value={formData.hostname}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="firmware_version" className="block text-sm font-medium text-gray-700">
                  Firmware Version
                </label>
                <input
                  type="text"
                  name="firmware_version"
                  id="firmware_version"
                  value={formData.firmware_version}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="ot_sentry_agent_version" className="block text-sm font-medium text-gray-700">
                  AI Sentry Agent Version
                </label>
                <input
                  type="text"
                  name="ot_sentry_agent_version"
                  id="ot_sentry_agent_version"
                  value={formData.ot_sentry_agent_version}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div className="col-span-6">
                <h4 className="font-medium text-gray-900 text-sm">Location Information</h4>
                <p className="text-xs text-gray-500 mb-2">Optional location details for this edge node</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="site" className="block text-sm font-medium text-gray-700">
                      Site
                    </label>
                    <input
                      type="text"
                      name="site"
                      id="site"
                      value={formData.location_info.site}
                      onChange={handleLocationChange}
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="building" className="block text-sm font-medium text-gray-700">
                      Building
                    </label>
                    <input
                      type="text"
                      name="building"
                      id="building"
                      value={formData.location_info.building}
                      onChange={handleLocationChange}
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="room" className="block text-sm font-medium text-gray-700">
                      Room
                    </label>
                    <input
                      type="text"
                      name="room"
                      id="room"
                      value={formData.location_info.room}
                      onChange={handleLocationChange}
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/edge-nodes')}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || success}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(submitting || success) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Registering...
                  </>
                ) : (
                  'Register Edge Node'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EdgeNodeRegistration;
